// Taşeron portalı — belge türü seçici + türün kuralına bağlı bitiş tarihi (GL #7; plan D8/D9,
// tasarım review §4.1/§4.2). Tür listesi §27 kaydındandır (`GET /document-types`); türe özgü istemci
// kodu yoktur, formun davranışı yalnız seçilen türün `expiryRule`'udur:
//   - `EndDateRequired` → bitiş tarihi görünür ve zorunludur (yıldız + kural ipucu);
//   - `EndDateOptional` → görünür, isteğe bağlıdır (kural ipucu);
//   - `NoExpiry` ya da tür seçilmemiş → alan GİZLİDİR; değer tür değişince boşaltılır ve ASLA
//     gönderilmez (sunucu okumaz).
// Varsayılan tür YOKTUR ("Belge türü seçin"); seçici yalnız AKTİF türleri sunar (`selectable`).
//
// Bu formlar RHF'siz ve kontrollüdür (`UploadModal` N grubu tek durumda tutar), bu yüzden web-expert'in
// RHF'ye bağlı `DocumentTypeField`/`ValidUntilField`'ı buraya uymaz (uygulamalar birbirini import
// etmez) — aynı durumlar burada kontrollü bileşenlerle kurulur.
//
// Kayıt durumu (yükleniyor / yüklenemedi / boş) seçicide DEĞİL `DocumentTypesStatus`'tadır: çoklu
// grupta TEK kez, grupların üstünde gösterilir (on grup = on Alert olmasın). Seçici yalnız
// yüklenirken "Yükleniyor…" yer tutucusuyla devre dışı kalır (Spinner değil — yükseklik kaymaz).
//
// Saf kural (`isSelectableDocumentType`, `validUntilToSend`, `missingRequiredValidUntil`,
// `withDocumentType`) web-expert ile ortaktır ve `@isgportal/shared`'dadır; çoklu grup durumu
// (değerler, tür reddi, odak) `DocumentFieldGroups.tsx`'tedir.

import {
  Alert,
  Button,
  type DocumentEntity,
  type DocumentTypeDto,
  Field,
  Input,
  Select,
  fieldDescribedBy,
  isSelectableDocumentType,
  missingRequiredValidUntil,
  requiresValidUntil,
  showsValidUntil,
  useDocumentTypes,
  useRefetchDocumentTypesOnMount,
} from '@isgportal/shared';
import type { ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';

/** Belge yükleme/bağlama alan grubunun değeri — `type` başlangıçta BOŞ (varsayılan tür yok). */
export interface DocumentFieldsValue {
  type: string;
  validFrom: string;
  validUntil: string;
  note: string;
}

export const EMPTY_DOCUMENT_FIELDS: DocumentFieldsValue = {
  type: '',
  validFrom: '',
  validUntil: '',
  note: '',
};

/** Grup kaydedilebilir mi: seçilebilir (aktif) bir tür + (kural istiyorsa) bitiş tarihi. */
export function documentFieldsComplete(
  types: readonly DocumentTypeDto[] | undefined,
  entity: DocumentEntity,
  values: Pick<DocumentFieldsValue, 'type' | 'validUntil'>,
): boolean {
  return (
    isSelectableDocumentType(types, entity, values.type) &&
    !missingRequiredValidUntil(types, entity, values)
  );
}

/** Seçilen türün tarih kuralı — görünürlük, zorunluluk ve kural ipucu (tür seçilmeden ipucu yok). */
export interface ExpiryRuleState {
  show: boolean;
  required: boolean;
  hint: string | undefined;
}

export function useExpiryRule(entity: DocumentEntity, code: string): ExpiryRuleState {
  const { t } = useTranslation();
  const { find } = useDocumentTypes();
  const type = code === '' ? undefined : find(entity, code);
  const show = showsValidUntil(type);
  return {
    show,
    required: requiresValidUntil(type),
    hint: type && show ? t(`documents.expiryHint.${type.expiryRule}`) : undefined,
  };
}

export interface DocumentTypeSelectProps {
  id: string;
  label: string;
  entity: DocumentEntity;
  /** Seçili kod; seçilebilir değilse (boş, pasifleşmiş) seçici yer tutucuyu gösterir. */
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  /** Çözülmüş alan hatası (sunucu reddi — `documentTypes.typeRejected`). */
  error?: string;
  /** Seçici altındaki açıklama (ör. §20.2'de pasif tanınan türün notu). */
  hint?: ReactNode;
  selectRef?: Ref<HTMLSelectElement>;
}

/** Kontrollü tür seçici: yalnız aktif türler, kayıt sırası; varsayılan yok. */
export function DocumentTypeSelect({
  id,
  label,
  entity,
  value,
  onChange,
  disabled,
  error,
  hint,
  selectRef,
}: DocumentTypeSelectProps) {
  const { t } = useTranslation();
  const registry = useDocumentTypes();
  const loading = registry.isPending;
  const options = registry
    .selectable(entity)
    .map((type) => ({ value: type.code, label: registry.label(entity, type.code) }));
  const unavailable = registry.types === undefined || options.length === 0;
  const shown = isSelectableDocumentType(registry.types, entity, value) ? value : '';
  return (
    <Field htmlFor={id} label={label} error={error} hint={hint} required>
      <Select
        ref={selectRef}
        id={id}
        options={options}
        placeholder={loading ? t('documentTypes.loading') : t('documentTypes.placeholder')}
        value={shown}
        disabled={disabled || unavailable}
        aria-busy={loading || undefined}
        aria-required="true"
        invalid={Boolean(error)}
        aria-describedby={fieldDescribedBy(id, error, hint !== undefined && hint !== null)}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export interface ValidUntilInputProps {
  id: string;
  label: string;
  rule: ExpiryRuleState;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Kural ipucunun yerine geçen ipucu (ör. §20.2'de tarih hesaplandıysa); verilmezse kural ipucu. */
  hint?: ReactNode;
}

/**
 * Bitiş tarihi — tür tarihi gizliyorsa (`NoExpiry`, tür yok) HİÇ render edilmez. Zorunluysa yıldız +
 * `aria-required`; kural ipucu `aria-describedby` ile bağlanır, böylece devre dışı bir "Kaydet"in
 * sebebi alanın yanında okunur.
 */
export function ValidUntilInput({
  id,
  label,
  rule,
  value,
  onChange,
  disabled,
  hint,
}: ValidUntilInputProps) {
  if (!rule.show) {
    return null;
  }
  const shownHint = hint ?? rule.hint;
  return (
    <Field htmlFor={id} label={label} hint={shownHint} required={rule.required}>
      <Input
        id={id}
        type="date"
        value={value}
        disabled={disabled}
        aria-required={rule.required || undefined}
        aria-describedby={fieldDescribedBy(id, undefined, shownHint !== undefined)}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

/**
 * Kaydın durumu — bir yükleme akışında TEK kez (çoklu grubun üstünde). Mount'ta kaydı bir kez tazeler
 * (D9, §27.7(2)); modal gövdesinde durduğu için her açılışta yeniden mount olur. Akışın girişi başka
 * bir yerde tazeleniyorsa (§20.2: sayfa girişi; içerik kalem başına remount olur) `refetchOnMount`
 * kapatılır. Hata yalnız `isError && types === undefined` iken gösterilir: önbellek varken başarısız bir
 * tazeleme SESSİZ kalır. Kayıt yüklü ama bu entity için aktif tür yoksa açıklama gösterilir.
 */
export function DocumentTypesStatus({
  entity,
  refetchOnMount = true,
}: {
  entity: DocumentEntity;
  refetchOnMount?: boolean;
}) {
  return refetchOnMount ? (
    <RefetchingDocumentTypesStatus entity={entity} />
  ) : (
    <DocumentTypesStatusView entity={entity} />
  );
}

function RefetchingDocumentTypesStatus({ entity }: { entity: DocumentEntity }) {
  useRefetchDocumentTypesOnMount();
  return <DocumentTypesStatusView entity={entity} />;
}

function DocumentTypesStatusView({ entity }: { entity: DocumentEntity }) {
  const { t } = useTranslation();
  const registry = useDocumentTypes();
  if (registry.isError && registry.types === undefined) {
    return (
      <Alert tone="danger">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span>{t('documentTypes.loadError')}</span>
          <Button variant="ghost" size="sm" onClick={() => void registry.refetch()}>
            {t('documentTypes.retry')}
          </Button>
        </div>
      </Alert>
    );
  }
  if (registry.types !== undefined && registry.selectable(entity).length === 0) {
    return <Alert tone="info">{t('documentTypes.empty')}</Alert>;
  }
  return null;
}
